/**
 * Client List Page
 *
 * Displays all clients with filtering, searching, and management capabilities.
 * Admin and technician access for client management.
 *
 * @module pages/clients/ClientList
 */

import React, { useState, useCallback, useDeferredValue, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '../../utils/toast';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Modal,
  Collapse,
  Dropdown,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { DataTable } from '../../components/common/DataTable';
import KebabMenu from '../../components/common/KebabMenu';
import { listClients, deleteClient } from '../../services/clientService';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { useAuth } from '../../contexts/AuthContext';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ClientIndustriesContent from '../settings/ClientIndustriesSettings';
import './ClientList.css';

/**
 * Format client type for display
 * @param {string} type - Client type
 * @returns {Object} Type info with label and variant
 */
const formatClientType = (type) => {
  // Las 8 claves son EXACTAMENTE el allowlist de `clients_client_type_check`.
  // `regular` y `vip` faltaban y sí existen en datos de producción: se
  // pintaban con el label crudo en inglés y el ícono de interrogación, como si
  // fueran un valor desconocido. Si se agrega un tipo, va primero la migración
  // del CHECK, después CLIENT_TYPE_OPTIONS en ClientForm.js y después acá.
  const types = {
    regular: { label: 'Regular', variant: 'secondary', icon: 'bi-building' },
    vip: { label: 'VIP', variant: 'dark', icon: 'bi-star-fill' },
    hospital: { label: 'Hospital', variant: 'danger', icon: 'bi-hospital' },
    clinic: { label: 'Clínica', variant: 'primary', icon: 'bi-plus-circle' },
    laboratory: { label: 'Laboratorio', variant: 'info', icon: 'bi-droplet' },
    pharmacy: { label: 'Farmacia', variant: 'success', icon: 'bi-capsule' },
    distributor: { label: 'Distribuidor', variant: 'warning', icon: 'bi-truck' },
    other: { label: 'Otro', variant: 'secondary', icon: 'bi-building' },
  };
  return types[type] || { label: type || 'Sin tipo', variant: 'secondary', icon: 'bi-question-circle' };
};

/**
 * Format client status for display
 * @param {string} status - Client status
 * @returns {Object} Status info with label and variant
 */
const formatClientStatus = (status) => {
  const statuses = {
    active: { label: 'Activo', variant: 'success' },
    inactive: { label: 'Inactivo', variant: 'secondary' },
    prospect: { label: 'Prospecto', variant: 'info' },
  };
  return statuses[status] || { label: status || 'Desconocido', variant: 'secondary' };
};

const ClientList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();
  const [activeTab, setActiveTab] = useState('listado');
  const [filters, setFilters] = useState({
    status: '',
    clientType: '',
    search: '',
    page: 0,
    pageSize: 20,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const searchTerm = useDeferredValue(searchInput);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Effective filters: use deferred searchTerm so typing doesn't block renders
  const effectiveFilters = useMemo(
    () => ({ ...filters, search: searchTerm }),
    [filters, searchTerm]
  );

  // Fetch clients with React Query
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['clients', effectiveFilters],
    // `listClients` cierra con `catch { return { data:null, count:0, error } }`:
    // RESUELVE. Sin el unwrap el `error` de react-query es estructuralmente
    // siempre null, el bloque `error ? <Alert>… Reintentar</Alert>` de abajo
    // queda inalcanzable y un corte de red se pinta como "No hay clientes
    // registrados" + "Crear primer cliente". Ver `src/utils/serviceResult.js`.
    queryFn: () => unwrapServiceResult(listClients(effectiveFilters)),
    staleTime: 1000 * 60 * 5, // 5 minutes
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new
  });

  // Delete client mutation
  //
  // El unwrap acá es MÁS importante que en la lectura: `deleteClient` también
  // RESUELVE con `{data:null, error}`, así que sin él la mutación entra por
  // `onSuccess` y festeja en verde "Cliente eliminado exitosamente" sobre un
  // DELETE que falló (RLS, red, 5xx). `onError` era código muerto. Un usuario
  // que cree que escribió es peor que uno que cree que no hay datos.
  const deleteMutation = useMutation({
    mutationFn: (clientId) => unwrapServiceResult(deleteClient(clientId)),
    onSuccess: () => {
      toast.success('Cliente eliminado exitosamente');
      queryClient.invalidateQueries(['clients']);
      setShowDeleteModal(false);
      setClientToDelete(null);
    },
    onError: (error) => {
      console.error('Delete client error:', error);
      toast.error('Error al eliminar cliente: ' + error.message);
    },
  });

  /**
   * Handle filter changes
   */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 0,
    }));
  };

  /**
   * Handle search input change — useDeferredValue handles the delay
   */
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);
    setFilters((prev) => ({ ...prev, page: 0 }));
  }, []);

  // Computed: count active filters (excluding search)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.clientType) count++;
    return count;
  }, [filters.status, filters.clientType]);

  const hasActiveFilters = activeFiltersCount > 0 || searchInput;

  /**
   * Handle clear all filters
   */
  const handleClearFilters = () => {
    setFilters((prev) => ({
      ...prev,
      status: '',
      clientType: '',
      page: 0,
    }));
    setSearchInput('');
  };

  /**
   * Toggle filters panel
   */
  const toggleFiltersPanel = () => setIsFiltersExpanded((prev) => !prev);

  /**
   * Handle row click
   */
  const handleRowClick = (clientId) => {
    navigate(`/clientes/${clientId}`);
  };

  /**
   * Handle create new client
   */
  const handleCreateClient = () => {
    navigate('/clientes/nuevo');
  };

  /**
   * Handle delete client - show confirmation modal
   */
  const handleDeleteClick = (e, client) => {
    e.stopPropagation();
    setClientToDelete(client);
    setShowDeleteModal(true);
  };

  /**
   * Confirm delete client
   */
  const confirmDelete = () => {
    if (clientToDelete) {
      deleteMutation.mutate(clientToDelete.id);
    }
  };

  const clients = data?.data || [];
  const totalCount = data?.count || 0;

  const clientColumns = useMemo(() => [
    {
      id: 'company',
      header: t('clients.companyName', 'Empresa'),
      accessor: 'company_name',
      minWidth: 220,
      cell: (client) => {
        const typeInfo = formatClientType(client.client_type);
        return (
          <div className="d-flex align-items-center">
            <div className="rounded-circle bg-primary bg-opacity-10 p-2 me-3"
              style={{ width: '40px', height: '40px' }}>
              <i className={`bi ${typeInfo.icon} text-primary`}></i>
            </div>
            <div>
              <div className="fw-semibold">{client.company_name}</div>
              {client.address_line1 && (
                <small className="text-muted">{client.address_line1}</small>
              )}
            </div>
          </div>
        );
      },
    },
    {
      id: 'type',
      header: t('clients.type', 'Tipo'),
      accessor: 'client_type',
      minWidth: 130,
      cell: (client) => {
        const typeInfo = formatClientType(client.client_type);
        return (
          <Badge bg={typeInfo.variant}>
            <i className={`bi ${typeInfo.icon} me-1`}></i>
            {typeInfo.label}
          </Badge>
        );
      },
    },
    {
      id: 'contact',
      header: t('clients.contact', 'Contacto'),
      accessor: (c) => c.primary_contact_name || '',
      minWidth: 180,
      cell: (client) => (
        <div>
          <div>{client.primary_contact_name || '-'}</div>
          {client.primary_contact_email && (
            <small className="text-muted">{client.primary_contact_email}</small>
          )}
        </div>
      ),
    },
    {
      id: 'phone',
      header: t('clients.phone', 'Teléfono'),
      accessor: (c) => c.primary_contact_phone || '',
      minWidth: 130,
      cell: (c) => c.primary_contact_phone || '-',
    },
    {
      id: 'status',
      header: t('clients.status', 'Estado'),
      accessor: 'status',
      minWidth: 110,
      cell: (client) => {
        const statusInfo = formatClientStatus(client.status);
        return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
      },
    },
    {
      id: 'actions',
      header: '',
      sortable: false,
      resizable: false,
      align: 'center',
      noSelect: true,
      minWidth: 50,
      maxWidth: 60,
      cell: (client) => (
        <div data-no-select onClick={(e) => e.stopPropagation()}>
          <KebabMenu label={`Acciones de ${client.company_name}`}>
            <Dropdown.Item onClick={() => navigate(`/clientes/${client.id}`)}>
              <i className="bi bi-eye me-2"></i>Ver Detalle
            </Dropdown.Item>
            <Dropdown.Item onClick={() => navigate(`/clientes/${client.id}/editar`)}>
              <i className="bi bi-pencil me-2"></i>Editar
            </Dropdown.Item>
            {isAdmin && (
              <>
                <Dropdown.Divider />
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => handleDeleteClick({ stopPropagation: () => {} }, client)}
                >
                  <i className="bi bi-trash me-2"></i>Eliminar
                </Dropdown.Item>
              </>
            )}
          </KebabMenu>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate, isAdmin, t]);

  return (
    <DashboardLayout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">
                  <i className="bi bi-building me-2"></i>
                  {t('clients.title', 'Clientes')}
                </h2>
                <p className="text-muted mb-0">
                  {t('clients.subtitle', 'Gestión de clientes y contactos')}
                </p>
              </div>
              {(isAdmin || isTechnician) && (
                <Button variant="primary" onClick={handleCreateClient}>
                  <i className="bi bi-plus-lg me-2"></i>
                  {t('clients.create', 'Nuevo Cliente')}
                </Button>
              )}
            </div>
          </Col>
        </Row>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
          className="mb-4"
          id="clients-tabs"
        >
          {/* Tab 1: Listado de Clientes */}
          <Tab
            eventKey="listado"
            title={
              <>
                <i className="bi bi-list-ul me-2"></i>
                {t('clients.tabListado', 'Listado')}
              </>
            }
          >
            <div className="pt-3">

        {/* Search & Filters */}
        <div className="client-search-filter mb-4">
          {/* Global search bar + filter toggle */}
          <div className="client-global-search">
            <InputGroup className="flex-grow-1">
              <InputGroup.Text>
                {isFetching && !isLoading ? (
                  <Spinner animation="border" size="sm" className="text-muted" />
                ) : (
                  <i className="bi bi-search"></i>
                )}
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('clients.searchPlaceholder', 'Buscar clientes...')}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                aria-label="Buscar en clientes"
              />
            </InputGroup>
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
            <Button
              variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
              onClick={toggleFiltersPanel}
              aria-expanded={isFiltersExpanded}
              aria-controls="client-advanced-filters"
              className="filter-toggle-btn"
            >
              <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
              <span className="d-none d-md-inline ms-1">{t('common.filters', 'Filtros')}</span>
              {activeFiltersCount > 0 && (
                <Badge bg="light" text="dark" pill className="filter-count-badge">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </div>

          {/* Collapsible advanced filters panel */}
          <Collapse in={isFiltersExpanded}>
            <div id="client-advanced-filters">
              <div className="advanced-filters-panel">
                <Row className="g-2 g-md-3">
                  <Col xs={12} sm={6} lg={4}>
                    <Form.Label className="small text-muted mb-1">
                      {t('clients.type', 'Tipo de cliente')}
                    </Form.Label>
                    <Form.Select
                      value={filters.clientType}
                      onChange={(e) => handleFilterChange('clientType', e.target.value)}
                      aria-label="Filtrar por tipo"
                      size="sm"
                    >
                      {/* Mismo allowlist que el CHECK y que el select del
                          formulario. `regular` y `vip` faltaban acá y son
                          valores reales en producción: el filtro no podía
                          encontrar a esos clientes ni ofreciéndose. */}
                      <option value="">{t('clients.allTypes', 'Todos los tipos')}</option>
                      <option value="regular">Regular</option>
                      <option value="vip">VIP</option>
                      <option value="hospital">Hospital</option>
                      <option value="clinic">Clínica</option>
                      <option value="laboratory">Laboratorio</option>
                      <option value="pharmacy">Farmacia</option>
                      <option value="distributor">Distribuidor</option>
                      <option value="other">Otro</option>
                    </Form.Select>
                  </Col>

                  <Col xs={12} sm={6} lg={4}>
                    <Form.Label className="small text-muted mb-1">
                      {t('clients.status', 'Estado')}
                    </Form.Label>
                    <Form.Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      aria-label="Filtrar por estado"
                      size="sm"
                    >
                      <option value="">{t('clients.allStatuses', 'Todos los estados')}</option>
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                      <option value="prospect">Prospecto</option>
                    </Form.Select>
                  </Col>
                </Row>

                {/* Clear filters button */}
                {hasActiveFilters && (
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleClearFilters}
                      className="clear-filters-btn"
                    >
                      <i className="bi bi-x-circle me-1"></i>
                      {t('common.clearFilters', 'Limpiar filtros')}
                      {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Collapse>
        </div>

        {/* Results */}
        <Card className="shadow-sm">
          <Card.Body>
            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">{t('common.loading', 'Cargando...')}</p>
              </div>
            ) : error ? (
              <Alert variant="danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {t('common.error', 'Error al cargar datos')}: {error.message}
                <Button variant="link" onClick={() => refetch()}>
                  {t('common.retry', 'Reintentar')}
                </Button>
              </Alert>
            ) : clients.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-building display-4 text-muted"></i>
                <p className="mt-3 text-muted">
                  {searchInput || filters.status || filters.clientType
                    ? t('clients.noResults', 'No se encontraron clientes con los filtros aplicados')
                    : t('clients.empty', 'No hay clientes registrados')}
                </p>
                {(isAdmin || isTechnician) && (
                  <Button variant="primary" onClick={handleCreateClient}>
                    <i className="bi bi-plus-lg me-2"></i>
                    {t('clients.createFirst', 'Crear primer cliente')}
                  </Button>
                )}
              </div>
            ) : (
              <>
                {isTableView ? (
                  <DataTable
                    tableId="clients"
                    data={clients}
                    rowKey={(c) => c.id}
                    onRowClick={(c) => handleRowClick(c.id)}
                    columns={clientColumns}
                  />
                ) : (
                <Row className="g-3">
                  {clients.map((client) => {
                    const typeInfo = formatClientType(client.client_type);
                    const statusInfo = formatClientStatus(client.status);
                    return (
                      <Col xs={12} sm={6} lg={4} key={client.id}>
                      <Card
                        className="client-card"
                        onClick={() => handleRowClick(client.id)}
                      >
                        <Card.Header className="d-flex align-items-center gap-3">
                          <div className="client-card-icon">
                            <i className={`bi ${typeInfo.icon}`}></i>
                          </div>
                          <div className="flex-grow-1">
                            <h6 className="client-card-company mb-1">{client.company_name}</h6>
                            <div className="client-card-badges">
                              <Badge bg={typeInfo.variant} className="me-1">
                                {typeInfo.label}
                              </Badge>
                              <Badge bg={statusInfo.variant}>
                                {statusInfo.label}
                              </Badge>
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <div className="client-card-info">
                            {client.primary_contact_name && (
                              <div className="client-card-info-row">
                                <i className="bi bi-person"></i>
                                <span>{client.primary_contact_name}</span>
                              </div>
                            )}
                            {client.primary_contact_email && (
                              <div className="client-card-info-row">
                                <i className="bi bi-envelope"></i>
                                <a
                                  href={`mailto:${client.primary_contact_email}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {client.primary_contact_email}
                                </a>
                              </div>
                            )}
                            {client.primary_contact_phone && (
                              <div className="client-card-info-row">
                                <i className="bi bi-telephone"></i>
                                <a
                                  href={`tel:${client.primary_contact_phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {client.primary_contact_phone}
                                </a>
                              </div>
                            )}
                            {client.projects_count !== undefined && (
                              <div className="client-card-info-row">
                                <i className="bi bi-folder"></i>
                                <span>
                                  {client.projects_count} {t('clients.projects', 'proyectos')}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="client-card-actions d-flex justify-content-end" onClick={(e) => e.stopPropagation()}>
                            <KebabMenu label={`Acciones de ${client.company_name}`}>
                              <Dropdown.Item onClick={() => navigate(`/clientes/${client.id}`)}>
                                <i className="bi bi-eye me-2"></i>
                                Ver Detalle
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => navigate(`/clientes/${client.id}/editar`)}>
                                <i className="bi bi-pencil me-2"></i>
                                Editar
                              </Dropdown.Item>
                              {isAdmin && (
                                <>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => handleDeleteClick({ stopPropagation: () => {} }, client)}
                                  >
                                    <i className="bi bi-trash me-2"></i>
                                    Eliminar
                                  </Dropdown.Item>
                                </>
                              )}
                            </KebabMenu>
                          </div>
                        </Card.Body>
                      </Card>
                      </Col>
                    );
                  })}
                </Row>
                )}

                {/* Pagination */}
                <div className="client-pagination d-flex justify-content-between align-items-center mt-3">
                  <span className="text-muted">
                    {t('common.showingOf', 'Mostrando')} {clients.length} {t('common.of', 'de')} {totalCount} {t('clients.clients', 'clientes')}
                  </span>
                  <div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={filters.page === 0}
                      onClick={() => handleFilterChange('page', filters.page - 1)}
                      className="me-2"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={(filters.page + 1) * filters.pageSize >= totalCount}
                      onClick={() => handleFilterChange('page', filters.page + 1)}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-exclamation-triangle text-warning me-2"></i>
              {t('clients.confirmDelete', 'Confirmar eliminación')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {clientToDelete && (
              <p>
                {t('clients.deleteMessage', '¿Estás seguro de eliminar el cliente')}{' '}
                <strong>{clientToDelete.company_name}</strong>?
                {t('clients.deleteWarning', ' Esta acción no se puede deshacer.')}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('common.deleting', 'Eliminando...')}
                </>
              ) : (
                <>
                  <i className="bi bi-trash me-2"></i>
                  {t('common.delete', 'Eliminar')}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
            </div>
          </Tab>

          {/* Tab 2: Industrias (admin only) — replica el patrón de
              "Tipos de Proyecto" en ProjectsUnifiedView (#461) */}
          {isAdmin && (
            <Tab
              eventKey="industrias"
              title={
                <>
                  <i className="bi bi-tags me-2"></i>
                  {t('clients.tabIndustrias', 'Industrias')}
                </>
              }
            >
              <div className="pt-3">
                <ClientIndustriesContent />
              </div>
            </Tab>
          )}
        </Tabs>
      </Container>
    </DashboardLayout>
  );
};

export default ClientList;
